/**
 * 持久化适配层
 * ------------------------------------------------------------------
 * 游戏原本运行在 Claude Artifacts 环境，使用宿主提供的异步 `window.storage`。
 * 本模块提供同样的异步接口，底层落到 localStorage，使游戏可在任意浏览器运行。
 *
 * 保持异步签名的好处：将来要换 IndexedDB、或接后端云存档（Supabase / 自建 API）
 * 时，只需替换本文件实现，游戏代码一行都不用动。
 *
 * 接口契约（与原宿主 API 一致）：
 *   get(key)    -> Promise<{ value: string } | null>   键不存在时返回 null
 *   set(key, v) -> Promise<void>
 *   delete(key) -> Promise<void>
 *   list(prefix)-> Promise<string[]>
 */

const PREFIX = ""; // 如需多存档位，可在此加命名空间前缀

function available() {
  try {
    const k = "__sanguo_probe__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false; // 无痕模式 / 禁用存储 / SSR
  }
}

const HAS_LS = typeof window !== "undefined" && available();

// localStorage 不可用时的内存兜底：本次会话内有效，刷新即失
const memory = new Map();

export const storage = {
  async get(key) {
    const k = PREFIX + key;
    try {
      const v = HAS_LS ? window.localStorage.getItem(k) : (memory.has(k) ? memory.get(k) : null);
      return v == null ? null : { key, value: v };
    } catch {
      return null;
    }
  },

  async set(key, value) {
    const k = PREFIX + key;
    const v = String(value);
    try {
      if (HAS_LS) window.localStorage.setItem(k, v);
      else memory.set(k, v);
    } catch (err) {
      // 配额超限等：降级到内存，避免游戏因存档失败而崩溃
      memory.set(k, v);
      console.warn("[storage] 持久化失败，已降级为内存存储：", err);
    }
  },

  async delete(key) {
    const k = PREFIX + key;
    try {
      if (HAS_LS) window.localStorage.removeItem(k);
      memory.delete(k);
    } catch {
      /* ignore */
    }
  },

  async list(prefix = "") {
    const out = [];
    try {
      if (HAS_LS) {
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith(PREFIX + prefix)) out.push(k.slice(PREFIX.length));
        }
      } else {
        for (const k of memory.keys()) {
          if (k.startsWith(PREFIX + prefix)) out.push(k.slice(PREFIX.length));
        }
      }
    } catch {
      /* ignore */
    }
    return out;
  },
};

/** 游戏使用的全部存档键，便于导入导出与清档 */
export const SAVE_KEYS = [
  "sanguo:save-v5",      // 沙盘战局存档
  "sanguo:settings-v5",  // 设置与图幅
  "sanguo:campaign-v1",  // 闯关进度
  "sanguo:story-v1",     // 故事进度、声望、难度
  "sanguo:grand-v1",     // 天下大局
  "sanguo:stats-v1",     // 终身战功簿
];

/** 导出全部存档为一个 JSON 字符串（备份 / 迁移设备用） */
export async function exportAll() {
  const dump = {};
  for (const k of SAVE_KEYS) {
    const r = await storage.get(k);
    if (r) dump[k] = r.value;
  }
  return JSON.stringify({ app: "sanguo-war-sandbox", exportedAt: Date.now(), data: dump }, null, 2);
}

/** 从 exportAll 产生的 JSON 恢复存档 */
export async function importAll(json) {
  const parsed = JSON.parse(json);
  const data = parsed?.data ?? {};
  for (const [k, v] of Object.entries(data)) {
    if (SAVE_KEYS.includes(k)) await storage.set(k, v);
  }
}

/** 清空全部存档 */
export async function clearAll() {
  for (const k of SAVE_KEYS) await storage.delete(k);
}
